import {
  Box,
  Button,
  Stack,
  Text,
  Theme,
  SxProps,
  styled,
} from '@ltvco/refresh-lib/theme';
import { nameize } from '@ltvco/refresh-lib/utils';
import { Record as RecordType } from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchURLParams,
  CollectionType,
  PersonSearchParamsv2,
} from '@ltvco/refresh-lib/v2';

const Image = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: 300,
  borderRadius: 8,
  objectFit: 'contain',
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

import {
  SummaryComponent,
  MessageTeaser,
  ReportSelectionPills,
  ReportSkeleton,
  TeaserSkeleton,
} from 'components/ChatBV/components';
import { SEARCH_TYPES, TRIGGER_SEARCH } from 'components/ChatBV/constants';
import {
  MessageReportData,
  PersonParamsFunctionArguments,
  PossibleReportParams,
} from 'components/ChatBV/types';
import { renderMessageContent } from 'components/ChatBV/utils';

import {
  PropertySummaryData,
  EmailSummaryData,
  VehicleReportSummaryData,
} from './SummaryComponent/DataTypes/SummaryTypes';

export const MessageContent = ({
  content,
  isHeader,
  isSticky,
  hasUserScrolled,
  handleButtonClick,
  handleChatTeaserClick,
  handleObituaryClick,
  handleAncestorClick,
  handleContactClick,
  handleFiltersClick,
  ContainerStyles,
  teaser,
  params,
  reports,
  currentBvid,
  currentPermalink,
  reportOpen,
  searchData,
  collectionType,
  setCollectionType,
  reportSummaryData,
  searchType,
  executedFunctionCall,
  imageUrl,
  possibleReports,
  onReportSelected,
  isLoadingReport,
  isLoadingTeaser,
  teaserSelectionMessage,
}: {
  content: string;
  isHeader: boolean;
  isSticky: boolean;
  hasUserScrolled: boolean;
  // eslint-disable-next-line no-unused-vars
  handleButtonClick: (report: MessageReportData) => void;
  // eslint-disable-next-line no-unused-vars
  handleChatTeaserClick: (bvid: string) => void;
  handleObituaryClick: (permalink: string) => void;
  handleAncestorClick: (permalink: string) => void;
  handleContactClick: (permalink: string) => void;
  handleFiltersClick: (params: PersonSearchParamsv2) => void;
  handleFollowUpButtonClick: () => void;
  ContainerStyles?: SxProps<Theme>;
  teaser: RecordType[] | undefined;
  params: PersonParamsFunctionArguments | undefined;
  reports: MessageReportData[] | undefined;
  messagePermalink: string | undefined;
  currentBvid: string | undefined;
  currentPermalink: string | undefined;
  reportOpen: boolean;
  searchData: AdvancedSearchURLParams;
  collectionType: CollectionType;
  setCollectionType: (collectionType: CollectionType) => void;
  reportSummaryData?: {
    property?: PropertySummaryData[];
    email?: EmailSummaryData[];
    vehicle?: VehicleReportSummaryData[];
    // TODO: Add other report types here
  };
  searchType?: string;
  executedFunctionCall?: string;
  imageUrl?: string;
  possibleReports?: PossibleReportParams[];
  onReportSelected?: (report: PossibleReportParams) => void;
  isLoadingReport?: boolean;
  isLoadingTeaser?: boolean;
  teaserSelectionMessage?: string; // Friendly message to display for teaser selections
}) => {
  const wasTriggerSearchExecuted =
    reports &&
    reports.length > 0 &&
    // Explicitly exclude GET_SPECIFIC_REPORT_SECTION
    executedFunctionCall !== 'get_specific_report_section' &&
    // Only show if it's TRIGGER_SEARCH or undefined (old messages with property/email)
    (executedFunctionCall === TRIGGER_SEARCH ||
      (executedFunctionCall === undefined &&
        (searchType === SEARCH_TYPES.PROPERTY ||
          searchType === SEARCH_TYPES.EMAIL ||
          searchType === SEARCH_TYPES.PHONE ||
          searchType === SEARCH_TYPES.VEHICLE ||
          searchType === SEARCH_TYPES.PERSON ||
          searchType === SEARCH_TYPES.OBITUARY ||
          searchType === SEARCH_TYPES.CONTACT ||
          searchType === SEARCH_TYPES.ANCESTOR)));

  const isSearchTypeSummary =
    !isHeader &&
    (wasTriggerSearchExecuted || teaser?.length) &&
    (searchType === SEARCH_TYPES.PROPERTY ||
      searchType === SEARCH_TYPES.EMAIL ||
      searchType === SEARCH_TYPES.PHONE ||
      searchType === SEARCH_TYPES.VEHICLE ||
      searchType === SEARCH_TYPES.PERSON ||
      searchType === SEARCH_TYPES.OBITUARY ||
      searchType === SEARCH_TYPES.CONTACT ||
      searchType === SEARCH_TYPES.ANCESTOR);

  const hasReportSummaryData =
    reportSummaryData &&
    Object.values(reportSummaryData).some(
      (reportArray) => Array.isArray(reportArray) && reportArray.length > 0
    );

  // Show summary ONLY if TRIGGER_SEARCH was executed (not GET_SPECIFIC_REPORT_SECTION),
  // it's property/email/phone/vehicle, and we have summary data
  const shouldShowSummary =
    isSearchTypeSummary && (hasReportSummaryData || teaser?.length);

  // Only hide the original content when we actually show a summary
  const shouldHideContent = shouldShowSummary;

  // For person reports, show button if TRIGGER_SEARCH was executed and we have reports
  const shouldShowPersonButton =
    !shouldShowSummary &&
    !isHeader &&
    searchType === SEARCH_TYPES.PERSON &&
    reports &&
    reports.length > 0;

  return (
    <Stack spacing={1} sx={ContainerStyles} mb={2}>
      {isLoadingReport ? (
        <ReportSkeleton />
      ) : shouldShowSummary ? (
        <SummaryComponent
          reportData={reportSummaryData || {}}
          teaser={teaser}
          params={params}
          currentPermalink={currentPermalink ?? ''}
          handleButtonClick={handleButtonClick}
          reportsAssociated={reports ?? []}
        />
      ) : shouldHideContent ? null : (
        <Box>
          {imageUrl && (
            <Box
              sx={{
                marginBottom: 2,
                display: 'inline-block',
              }}
            >
              <Image src={imageUrl} alt="Uploaded image" />
            </Box>
          )}
          <Text
            variant={isHeader ? 'h1' : 'body1'}
            sx={{
              fontWeight: isHeader ? 600 : 400,
              color: 'text.primary',
              whiteSpace: 'pre-wrap',
              marginBottom: isHeader ? '0' : '20px',
              marginTop: isHeader ? '0' : '10px',
              lineHeight: 1.5,
              fontSize: isHeader
                ? isSticky && hasUserScrolled
                  ? '24px !important'
                  : '30px !important'
                : undefined,
              transition: isHeader ? 'font-size 0.3s ease-in-out' : undefined,
              '& *': {
                fontSize: 'inherit !important',
              },
            }}
          >
            {renderMessageContent(teaserSelectionMessage || content)}
          </Text>
          {possibleReports &&
            possibleReports.length > 1 &&
            onReportSelected && (
              <ReportSelectionPills
                possibleReports={possibleReports}
                onReportSelected={onReportSelected}
              />
            )}
          {shouldShowPersonButton &&
            reports &&
            (reports.length === 1 ? (
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleButtonClick(reports[0])}
                  disabled={currentPermalink === reports[0].permalink}
                >
                  View full report
                </Button>
              </Box>
            ) : (
              <Box>
                {reports.map((report) => (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleButtonClick(report)}
                    disabled={currentPermalink === report.permalink}
                    key={report.permalink}
                  >
                    {nameize(report.type)}
                  </Button>
                ))}
              </Box>
            ))}
        </Box>
      )}
      {isLoadingTeaser ? (
        <TeaserSkeleton />
      ) : (
        teaser && (
          <MessageTeaser
            teaser={teaser}
            params={params}
            currentBvid={currentBvid || ''}
            reportOpen={reportOpen}
            searchData={searchData}
            collectionType={collectionType}
            setCollectionType={setCollectionType}
            handleChatTeaserClick={handleChatTeaserClick}
            handleObituaryClick={handleObituaryClick}
            handleAncestorClick={handleAncestorClick}
            handleContactClick={handleContactClick}
            handleFiltersClick={handleFiltersClick}
          />
        )
      )}
    </Stack>
  );
};
